import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Award, Wrench, Clock, Phone, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { features, services as mockServices } from '../data/mock';
import { getServices } from '../services/productApi';

/**
 * ContentPageLayout - Wiederverwendbare Layout-Komponente für statische Seiten
 * 
 * Enthält die gemeinsamen Abschnitte:
 * - 4 Vorteile (Features)
 * - 3 Leistungen (Services)
 * - CTA Banner
 * - 2 Videos
 * 
 * @param {ReactNode} children - Der Hauptinhalt der Seite (vor den gemeinsamen Abschnitten)
 * @param {boolean} showFeatures - Zeige 4 Vorteile-Karten (default: true)
 * @param {boolean} showServices - Zeige 3 Leistungen-Karten (default: true)
 * @param {boolean} showCTA - Zeige CTA-Banner (default: true)
 * @param {boolean} showVideos - Zeige 2 Videos (default: true)
 */
const ContentPageLayout = ({ 
  children, 
  showFeatures = true, 
  showServices = true, 
  showCTA = true, 
  showVideos = true 
}) => {
  const { t } = useTranslation();
  const [services, setServices] = useState(mockServices);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const servicesData = await getServices();
        if (servicesData?.length > 0) {
          setServices(servicesData);
        }
      } catch (error) {
        console.error('Error loading services:', error);
      }
    };
    if (showServices) {
      loadServices();
    }
  }, [showServices]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hauptinhalt der Seite */}
      {children}

      {/* Info-Bereich: 4 Vorteile + 3 Leistungen + CTA */}
      {(showFeatures || showServices || showCTA) && (
        <section className="py-6 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
            <div className="bg-gray-50 rounded-xl p-4 sm:p-6 space-y-4 sm:space-y-6">
              
              {/* 4 Vorteile */}
              {showFeatures && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                  {features.map((feature, index) => {
                    const IconComponent = {
                      Award,
                      Wrench,
                      Clock,
                      HeadphonesIcon: Phone
                    }[feature.icon];
                    
                    return (
                      <Card key={index} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                        <CardContent className="pt-6 pb-6">
                          <div className="flex flex-col items-center text-center">
                            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                              <IconComponent className="text-red-600" size={24} />
                            </div>
                            <h3 className="font-semibold text-sm sm:text-lg mb-2">{feature.title}</h3>
                            <p className="text-gray-600 text-xs sm:text-sm">{feature.description}</p>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* 3 Leistungen */}
              {showServices && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                  {services.map((service) => (
                    <Card key={service.id} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                      <CardContent className="pt-6 pb-6">
                        <div className="flex flex-col items-center text-center">
                          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                            {service.id === 1 && <Award className="text-red-600" size={24} />}
                            {service.id === 2 && <Phone className="text-red-600" size={24} />}
                            {service.id === 3 && <Wrench className="text-red-600" size={24} />}
                          </div>
                          <h3 className="font-semibold text-sm sm:text-lg mb-2">{service.title}</h3>
                          <p className="text-gray-600 text-xs sm:text-sm mb-3">{service.description}</p>
                          <ul className="space-y-1 text-left w-full">
                            {service.features.slice(0, 3).map((feat, idx) => (
                              <li key={idx} className="flex items-start text-xs sm:text-sm">
                                <CheckCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={14} />
                                <span className="text-gray-700">{feat}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* CTA */}
              {showCTA && (
                <Card className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                  <CardContent className="pt-8 pb-8">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                        <ArrowRight className="text-red-600" size={24} />
                      </div>
                      <h3 className="font-semibold text-lg sm:text-xl mb-2">{t('cta.readyForProject')}</h3>
                      <p className="text-gray-600 text-sm sm:text-base mb-4 max-w-xl">
                        {t('cta.contactUs')}
                      </p>
                      <Link to="/kontakt">
                        <Button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center">
                          {t('cta.getInTouch')}
                          <ArrowRight className="ml-2" size={20} />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              )}

            </div>
          </div>
        </section>
      )}

      {/* Zwei Videos */}
      {showVideos && (
        <section className="py-6 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <video autoPlay muted loop playsInline preload="metadata" className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover" webkit-playsinline="true">
                  <source src="/videos/video2.mp4" type="video/mp4" />
                </video>
              </div>
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <video autoPlay muted loop playsInline preload="metadata" className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover" webkit-playsinline="true">
                  <source src="/videos/video3.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ContentPageLayout;
